import { DATE_FORMAT } from '@web/config/constants';
import {
    differenceInCalendarDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    format,
    isValid,
    parseISO,
} from 'date-fns';

// Returns a formatted date string based on the provided date and format.
export function formatDate(date: Date | undefined | string, dateFormat: string = DATE_FORMAT.MM_DD_YEAR): string {
    let dateObj: Date;

    // Handle invalid or missing date
    if (!date) {
        throw new Error('Invalid date');
    }

    // Parse string to Date
    if (typeof date === 'string') {
        dateObj = parseISO(date);
    } else if (date instanceof Date) {
        dateObj = date;
    } else {
        throw new Error('Unexpected date type');
    }

    // Check if the date object is valid
    if (!isValid(dateObj)) {
        throw new Error('Invalid date');
    }

    return format(dateObj, dateFormat);
}

export type TimeAgo = { count: number; unit: TimeAgoUnit };

export type TimeAgoUnit = 'day' | 'hour' | 'minute' | 'month' | 'year';

type TimeAgoUnitMapping = { plural: string; singular: string };

const TIME_AGO_UNIT_MAP: Record<TimeAgoUnit, TimeAgoUnitMapping> = {
    day: { plural: 'Days', singular: 'Day' },
    hour: { plural: 'Hours', singular: 'Hour' },
    minute: { plural: 'Minutes', singular: 'Minute' },
    month: { plural: 'Months', singular: 'Month' },
    year: { plural: 'Years', singular: 'Year' },
};

/**
 * Formats a date and follows it with how long ago it was: "Oct 5, 2025 (2 Days ago)".
 */
export function formatDateWithTimeAgo(
    date: Date | string | undefined,
    dateFormat: string = DATE_FORMAT.MM_DD_YEAR,
    defaultValue: string = 'N/A',
    now: Date = new Date()
): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return defaultValue;
    }

    return `${format(parsedDate, dateFormat)} (${formatTimeAgo(parsedDate, defaultValue, now)})`;
}

/**
 * Turns a date into how long ago it was, from "Just now" up to years.
 *
 * The unit follows the size of the gap, so one timestamp reads the same whether it is minutes or years old.
 */
export function formatTimeAgo(
    date: Date | string | undefined,
    defaultValue: string = 'N/A',
    now: Date = new Date()
): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return defaultValue;
    }

    return toTimeAgoLabel(toTimeAgo(parsedDate, now));
}

/**
 * Reduces the gap between two dates to a single unit and a count.
 *
 * Kept apart from the wording so the same measurement can be written several ways — long, short, capitalised —
 * without a second copy of the ladder, which is how the app ended up with more than one of these.
 */
export function toTimeAgo(date: Date, now: Date): TimeAgo {
    const minutes = differenceInMinutes(now, date);

    if (minutes < 1) {
        return { count: 0, unit: 'minute' };
    }

    if (minutes < 60) {
        return { count: minutes, unit: 'minute' };
    }

    const hours = differenceInHours(now, date);

    if (hours < 24) {
        return { count: hours, unit: 'hour' };
    }

    const days = differenceInCalendarDays(now, date);

    // Full months elapsed, not calendar months crossed, or a two-week gap over a month boundary reads as a month.
    const months = differenceInMonths(now, date);

    if (months < 1) {
        return { count: days, unit: 'day' };
    }

    if (months < 12) {
        return { count: months, unit: 'month' };
    }

    // Divided out of the month count so the two tiers can never disagree, which they do across a Feb 29.
    return { count: Math.floor(months / 12), unit: 'year' };
}

/**
 * Writes a measured gap as English prose: "Just now", "Yesterday", "2 Days ago".
 *
 * One day means the previous calendar day, because anything inside twenty-four hours is counted in hours and never
 * reaches the day tier.
 */
export function toTimeAgoLabel({ count, unit }: TimeAgo): string {
    if (unit === 'minute' && count === 0) {
        return 'Just now';
    }

    if (unit === 'day' && count === 1) {
        return 'Yesterday';
    }

    const { plural, singular } = TIME_AGO_UNIT_MAP[unit];

    if (count === 1) {
        return `${count} ${singular} ago`;
    }

    return `${count} ${plural} ago`;
}

export function parseDate(value: Date | string | undefined): Date | null {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }

    const parsedDate = parseISO(value);
    return isValid(parsedDate) ? parsedDate : null;
}
